import React, { useCallback, useMemo, useState } from 'react';
import { Modal, Button } from 'antd';
import { useDispatch } from 'react-redux';
import { toggleBootcampModal } from '@redux/actions/modalAction';
import { redirectToSubdomain } from '@components/OrganizationBoundary';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import MainImage from '@assets/images/welcome/image.jpg';
import { ReactComponent as VideoSvg } from '@assets/images/welcome/video.svg';
import { ReactComponent as BootcampSvg } from '@assets/images/welcome/bootcamp.svg';
import WatchVideoModal from '@components/Modals/BootcampModal/components/WatchVideoModal/WatchVideoModal';
import { DEMO_SUBDOMAIN, DEMO_TOUR_WORKSPACE_ID } from '@config/constants';

import './WelcomeModal.less';

export type WelcomeModalProps = {
  onCloseModal: (tourType?: string) => void;
  open: boolean;
};

export default function WelcomeModal({
  open,
  onCloseModal
}: WelcomeModalProps) {
  const dispatch = useDispatch();

  const onClose = useCallback(() => {
    onCloseModal('tour-paused');
  }, [onCloseModal]);

  const [isShowVideoModal, setIsShowVideoModal] = useState(false);

  const items = useMemo(() => {
    return [
      {
        icon: <VideoSvg />,
        title: 'Watch Video Tour',
        onClick: () => {
          setIsShowVideoModal(true);
        },
        text: (
          <>
            Browse short guided videos and
            <br /> learn StreamWork in minutes.
          </>
        )
      },
      {
        icon: <BootcampSvg />,
        title: 'Go to Bootcamp',
        onClick: () => {
          onCloseModal();
          dispatch(
            toggleBootcampModal({
              visible: true
            })
          );
        },
        text: (
          <>
            Follow an interactive
            <br /> walkthrough at your own pace.
          </>
        )
      }
    ];
  }, [setIsShowVideoModal, dispatch, onCloseModal]);

  const onConfirm = useCallback(() => {
    onCloseModal();
    redirectToSubdomain(
      DEMO_SUBDOMAIN,
      `campaigns/all/${DEMO_TOUR_WORKSPACE_ID}`,
      [{ name: 'startTour', value: 'route-for-approval' }]
    );
  }, [onCloseModal]);

  return (
    <>
      <Modal
        open={open}
        footer={null}
        centered={true}
        closeIcon={<CrossSvg />}
        destroyOnClose
        width={955}
        maskClosable={false}
        onCancel={onClose}
        className="welcome-modal"
      >
        <div className="modal_container">
          <div className="welcome-modal_title">Welcome to StreamWork!</div>
          <div className="welcome-modal_text">
            Choose what you want to do first.
          </div>
          <div className="welcome-modal_content">
            <div className="welcome-modal_left">
              <div className="welcome-modal_image">
                <img src={MainImage} alt="Welcome to StreamWork!" />
              </div>
              <div className="welcome-modal_subtitle">
                Learn how to route for approval
              </div>
              <div className="welcome-modal_text">
                Take a quick tour to see how easy it is to route
                <br /> media for review in StreamWork.
              </div>
              <div className="welcome-modal_buttons">
                <Button
                  type="primary"
                  onClick={onConfirm}
                  style={{ minWidth: 145 }}
                >
                  Start Here
                </Button>
                <Button type="text" onClick={onClose}>
                  Skip for now
                </Button>
              </div>
            </div>
            <div className="welcome-modal_right">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="welcome-modal_block"
                  onClick={item?.onClick}
                >
                  <div className="welcome-modal_block-icon">{item.icon}</div>
                  <div className="welcome-modal_block-title">{item.title}</div>
                  <div className="welcome-modal_block-text">{item.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>
      <WatchVideoModal
        visible={isShowVideoModal ? 'intro' : false}
        setVisible={() => setIsShowVideoModal(false)}
        title="Welcome to StreamWork!"
      />
    </>
  );
}
